import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { CalendarClock } from "lucide-react";
import { PsvMonitoringProgramStatus } from "@api/shared-runtime/psv-monitoring";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { useOrgPath } from "@/hooks/useOrgPath";
import { retryUnlessRecordUnavailable } from "@/common/utils/record-unavailable-error";
import { useTRPC } from "@/trpc";
import { EnableMonitoringDialog } from "./enable-monitoring-dialog";
import {
  FEATURE_NAME,
  MONITORING_COPY,
  MonitoringBadgeKind,
} from "./monitoring-constants";
import { isForbiddenError, plural } from "./monitoring-format";
import type { PoolMonitoring } from "./monitoring-types";
import { MonitoringStatusBadge } from "./monitoring-status-badge";
import { TurnOffPoolDialog } from "./pool-monitoring-bar/turn-off-pool-dialog";

type Props = {
  organizationBusinessUnitId: string;
  providerPoolId: string;
  poolName: string;
  /** The user may edit provider credentials, which turning monitoring on or off needs. */
  canEdit: boolean;
};

/** Monitoring status under the pool header. Render it only when primary
 * source verification is enabled for the business unit. Users restricted to
 * specific pools get FORBIDDEN from the pool endpoints, and see nothing. */
export function PoolMonitoringBar({
  organizationBusinessUnitId,
  providerPoolId,
  poolName,
  canEdit,
}: Props) {
  const trpc = useTRPC();
  const orgPath = useOrgPath();
  const [enableOpen, setEnableOpen] = useState(false);
  const [turnOffOpen, setTurnOffOpen] = useState(false);
  const query = useQuery(
    trpc.psv.monitoring.getPoolMonitoring.queryOptions(
      { organizationBusinessUnitId, providerPoolId },
      { retry: retryUnlessRecordUnavailable },
    ),
  );
  if (isForbiddenError(query.error)) {
    return null;
  }
  if (query.isPending) {
    return (
      <Card role="status" aria-busy="true" className="mb-6">
        <span className="sr-only">Loading monthly monitoring…</span>
        <CardContent
          aria-hidden
          className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between sm:gap-6"
        >
          <div className="space-y-1">
            <Skeleton className="h-5 w-48" />
            <Skeleton className="h-5 w-72 max-w-full" />
          </div>
          <Skeleton className="h-8 w-40" />
        </CardContent>
      </Card>
    );
  }
  if (query.isError) {
    return (
      <Card className="mb-6">
        <CardContent className="flex items-center justify-between gap-2 p-4 text-sm">
          <p role="alert">Couldn't load monthly monitoring.</p>
          <Button
            variant="outline"
            size="sm"
            onClick={() => void query.refetch()}
          >
            Try again
          </Button>
        </CardContent>
      </Card>
    );
  }
  const pool = query.data;
  const isOn = pool.rules.length > 0;
  return (
    <Card className="mb-6">
      <CardContent className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between sm:gap-6">
        <div className="min-w-0 space-y-1">
          <div className="flex flex-wrap items-center gap-2">
            <CalendarClock className="size-4 text-primary" aria-hidden="true" />
            <h2 className="text-sm font-semibold text-slate-900">
              {FEATURE_NAME}
            </h2>
            <MonitoringStatusBadge kind={poolBadgeKind(pool)} />
          </div>
          <PoolMonitoringDetails pool={pool} />
        </div>
        <div className="flex shrink-0 flex-wrap items-center gap-2 sm:justify-end">
          <Button
            asChild
            variant="link"
            size="sm"
            className="max-sm:h-11 max-sm:px-0"
          >
            <Link to={orgPath("/providers/credentials/settings")}>
              {MONITORING_COPY.manageRules}
            </Link>
          </Button>
          {canEdit && !isOn && (
            <Button
              size="sm"
              className="max-sm:h-11"
              onClick={() => setEnableOpen(true)}
            >
              {MONITORING_COPY.turnOn}
            </Button>
          )}
          {canEdit && isOn && (
            <Button
              variant="outline"
              size="sm"
              className="max-sm:h-11"
              onClick={() => setTurnOffOpen(true)}
            >
              {MONITORING_COPY.turnOff}
            </Button>
          )}
        </div>
      </CardContent>
      {enableOpen && (
        <EnableMonitoringDialog
          organizationBusinessUnitId={organizationBusinessUnitId}
          subjectName={poolName}
          target={{ providerPoolId }}
          onClose={() => setEnableOpen(false)}
        />
      )}
      {turnOffOpen && (
        <TurnOffPoolDialog
          organizationBusinessUnitId={organizationBusinessUnitId}
          providerPoolId={providerPoolId}
          poolName={poolName}
          programIds={pool.rules.map((rule) => rule.programId)}
          onClose={() => setTurnOffOpen(false)}
        />
      )}
    </Card>
  );
}

function poolBadgeKind(pool: PoolMonitoring): MonitoringBadgeKind {
  if (pool.rules.length === 0) {
    return MonitoringBadgeKind.NotMonitored;
  }
  const anyActive = pool.rules.some(
    (rule) => rule.status === PsvMonitoringProgramStatus.Active,
  );
  return anyActive ? MonitoringBadgeKind.Monitored : MonitoringBadgeKind.Paused;
}

function PoolMonitoringDetails({ pool }: { pool: PoolMonitoring }) {
  if (pool.rules.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        Re-check every member's credentials each month.
      </p>
    );
  }
  const ruleNames = pool.rules.map((rule) => rule.name).join(", ");
  return (
    <div className="space-y-0.5 text-sm text-muted-foreground">
      <p>
        {pool.monitoredCount} of{" "}
        {plural({ count: pool.memberCount, noun: "member" })} monitored
        {pool.unavailableCount > 0 &&
          ` · ${pool.unavailableCount} can't be monitored`}
        {" · New members are included automatically."}
      </p>
      <p>
        Rule: <span className="text-slate-900">{ruleNames}</span>
      </p>
    </div>
  );
}
